import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import {
    DEFAULT_SYMBOL, Marker,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/marker';
import {
    Axis,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/axis';
import type { ChartUIDataItem, CustomStyles, ShapeStyle } from '../interface/chart-ui.interface';
import type { IGeometryOptions, IChartRendererConfig } from '@tencent/chart/lib/chart-ui/interface';
import { AxisGridCfg } from '@tencent/chart/lib/antv/g2/interface';
import { ShapeOriginStyle } from '../interface/chart-ui.interface';
import {
    filterOriginStyle,
    generateCustomStyles,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';

export class RadarChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.RADAR_CHART;
    }

    public getRenderConfig(): IChartRendererConfig {
        const baseConfig = super.getRenderConfig();

        return {
            ...baseConfig,
            radarChartConfig: {
                pointVisible: this.pointVisible,
                // area目前通过line的Fill实现，但是line的Fill没有事件响应，这里通过area实现事件响应
                areaVisible: this.areaVisible,
            },
        };
    }

    protected getAxis() {
        return new RadarAxis(this.subChart, this.context.getChartModel()).init();
    }

    protected getGeometryOptions(): IGeometryOptions {
        return {
            geometryStyles: this.getGeometryStyles(),
            geometrySizes: this.getGeometrySizes(),
            geometryShapes: this.getGeometryShapes(),
        };
    }

    private getGeometryStyles() {
        return {
            line: this.createIdMappingCfg((chartDataItem) => {
                const { fill, border } = chartDataItem || {};
                return this.filterShapeAttr({
                    fill,
                    fillOpacity: 1,
                    // g2多图形绘制，按照图形进行进行分层
                    // 如果绘制完area再绘制line，会导致重叠部分下层的线条显示
                    // 如果绘制完line在绘制area，会导致重叠部分上层的线条不显示
                    // 如果使用area加stroke， 会从中心多出一条线
                    // 所以最终用line 加fill
                    lineWidth: border?.lineWidth,
                    lineDash: border?.lineDash,
                    stroke: border?.stroke,
                    lineJoin: border?.lineJoin,
                    lineCap: border?.lineCap,
                    customStyles: this.getAreaCustomStyles(chartDataItem),
                });
            }),
            area: this.createIdMappingCfg(() => ({
                // area 设置成透明的，主要是用于处理事件
                fill: 'transparent',
                fillOpacity: 0,
            })),
            point: this.createFieldMappingCfg<ShapeStyle | undefined>((name) => {
                const series = this.series.find(item => item.name === name);
                if (!series?.marker) return;
                const { fill, border } = series.marker;

                return this.filterShapeAttr({
                    fill,
                    lineWidth: border?.lineWidth,
                    lineDash: border?.lineDash,
                    stroke: border?.stroke,
                    lineJoin: border?.lineJoin,
                    lineCap: border?.lineCap,
                    customStyles: this.getPointCustomStyles(series.marker),
                });
            }, 'name'),
        };
    }

    private getAreaCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        return generateCustomStyles(chartDataItem);
    }

    private getPointCustomStyles(originStyle: ShapeOriginStyle): CustomStyles {
        return generateCustomStyles(originStyle);
    }

    private getGeometrySizes() {
        return {
            point: this.createIdMappingCfg<number | undefined>((chartDataItem) => {
                if (!this.hasMarker(chartDataItem)) return 0;
                const { marker } = chartDataItem;
                return marker?.size;
            }),
        };
    }

    private getGeometryShapes() {
        return {
            point: this.createIdMappingCfg<Marker | string>((chartDataItem) => {
                const { marker } = chartDataItem;
                return marker?.symbol || DEFAULT_SYMBOL;
            }),
        };
    }

    private get pointVisible(): boolean {
        return this.isShowMarker(this.series);
    }
    private get lineVisible(): boolean {
        return this.isShowBorder(this.series);
    }
    private get areaVisible(): boolean {
        return this.isShowFill(this.series);
    }

    private isShowBorder(series): boolean {
        return series.some(item => !isEmpty(item.border));
    }
    private isShowFill(series): boolean {
        return series.some(item => !isEmpty(item.fill));
    }
}

class RadarAxis extends Axis {
    protected getGrid(axis, type: 'xAxis' | 'yAxis'): AxisGridCfg | null {
        const grid = super.getGrid(axis, type);
        if (!grid) return grid;
        if (type === 'yAxis') {
            set(grid, 'line.type', 'line');
        } else if (type === 'xAxis') {
            return null;
        }
        return grid;
    }
}
